// Earnings: what each rep has made and is owed. First month in full,
// then 10% a month for as long as the store pays (salesCommission.js).
// Three parts: a card per rep, the deals behind the numbers, and the
// monthly statement where Phillip marks each rep's month as paid.
import React, { useMemo, useState } from 'react';
import { AlertTriangle, Check, CircleDollarSign, DollarSign, RefreshCw } from 'lucide-react';
import { RESIDUAL_RATE, closerOf, dealEarnings, dealIncomplete, isDeal, money, periodLabel, planLabel, repEarnings, statement, statementTotals } from './salesCommission';
import { markPaid, unmarkPaid } from './salesData';
import { Empty, REP_COLORS, RepChip, btnGhost, btnSmall } from './salesUi';

const fmtDay = (iso) => (iso ? new Date(`${String(iso).slice(0, 10)}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '');

export default function EarningsView({ prospects, reps, repById, me, payouts, onPayoutsChange, client, storeId, onOpen, notify, dealsReady, onRetry }) {
  const [busyKey, setBusyKey] = useState('');
  const [repFilter, setRepFilter] = useState('all');
  const now = new Date();

  const deals = useMemo(() => prospects.filter((p) => isDeal(p)).sort((a, b) => String(b.billing_start).localeCompare(String(a.billing_start))), [prospects]);
  const incomplete = useMemo(() => prospects.filter(dealIncomplete), [prospects]);
  const perRep = useMemo(() => reps.map((r) => ({ rep: r, ...repEarnings(prospects, r.id, now) })).sort((a, b) => b.earned - a.earned), [prospects, reps]); // eslint-disable-line react-hooks/exhaustive-deps
  const rows = useMemo(() => statement(prospects, reps, payouts, now).filter((r) => repFilter === 'all' || r.repId === repFilter), [prospects, reps, payouts, repFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  const totals = statementTotals(rows);
  const team = perRep.reduce((t, r) => ({ earned: t.earned + r.earned, residual: t.residual + r.residual, active: t.active + r.active }), { earned: 0, residual: 0, active: 0 });

  if (!dealsReady) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 flex gap-4">
        <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="min-w-0">
          <h2 className="text-lg font-bold text-slate-900">Commissions are not set up yet</h2>
          <p className="text-sm text-slate-700 mt-1">Run the SQL below in the Supabase SQL editor once, then reload. It adds five deal columns to the prospects table and a payouts table, and touches nothing else.</p>
          <pre className="mt-3 text-xs bg-white border border-amber-200 rounded-lg px-3 py-2 overflow-x-auto">sql/sales-pipeline/04_commissions.sql</pre>
          <button type="button" onClick={onRetry} className={`${btnGhost} mt-4`}><RefreshCw className="w-4 h-4" /> Check again</button>
        </div>
      </div>
    );
  }

  const togglePaid = async (r) => {
    const key = `${r.repId}|${r.period}`;
    if (busyKey) return;
    setBusyKey(key);
    try {
      if (r.payout) {
        await unmarkPaid(client, r.payout.id);
        onPayoutsChange(payouts.filter((q) => q.id !== r.payout.id));
        notify(`${r.rep?.name || 'Rep'} ${periodLabel(r.period)} marked unpaid`);
      } else {
        const row = await markPaid(client, { store_id: storeId, rep_id: r.repId, period: r.period, amount: r.amount, paid_at: new Date().toISOString() });
        onPayoutsChange([row, ...payouts]);
        notify(`${money(r.amount)} to ${r.rep?.name || 'rep'} for ${periodLabel(r.period)} marked paid`);
      }
    } catch (e) {
      console.error('sales: payout failed', e);
      notify('Could not save that', 'error');
    } finally {
      setBusyKey('');
    }
  };

  return (
    <div className="space-y-4">
      {/* the rule */}
      <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="inline-flex items-center gap-1.5 font-bold text-slate-900"><DollarSign className="w-4 h-4 text-emerald-600" /> The rule</span>
        <span>Close a store: the <span className="font-semibold">first month's payment is yours</span>, then <span className="font-semibold">{Math.round(RESIDUAL_RATE * 100)}% of every month</span> for as long as they stay. Setup fees are not commissioned.</span>
      </div>

      {incomplete.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex flex-wrap items-center gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>{incomplete.length} customer{incomplete.length === 1 ? ' has' : 's have'} no monthly amount yet, so nothing is counted for {incomplete.length === 1 ? 'it' : 'them'}:</span>
          {incomplete.map((p) => <button key={p.id} type="button" onClick={() => onOpen(p.id)} className="font-semibold underline">{p.name}</button>)}
        </div>
      )}

      {/* per rep */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {perRep.map(({ rep, ...e }) => (
          <div key={rep.id} className={`rounded-2xl border bg-white p-4 ${me?.id === rep.id ? 'border-blue-300 ring-2 ring-blue-500/10' : 'border-slate-200'}`}>
            <div className="flex items-center gap-2">
              <RepChip rep={rep} size="md" />
              <span className="font-bold text-slate-900 truncate">{rep.name}</span>
              <span className="ml-auto text-[11px] text-slate-400">{e.deals} deal{e.deals === 1 ? '' : 's'} · {e.active} paying</span>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Earned so far</div>
                <div className="text-2xl font-bold text-slate-900 leading-tight">{money(e.earned)}</div>
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Every month now</div>
                <div className="text-2xl font-bold text-emerald-700 leading-tight">{money(e.residual)}</div>
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">This month</div>
                <div className="text-base font-bold text-slate-800">{money(e.thisMonth)}</div>
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">First year of these deals</div>
                <div className="text-base font-bold text-slate-800">{money(e.firstYear)}</div>
              </div>
            </div>
            <div className="mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden">
              <div className={`h-full rounded-full ${REP_COLORS[rep.color] || REP_COLORS.blue}`} style={{ width: `${team.earned ? Math.max(3, Math.round((e.earned / team.earned) * 100)) : 0}%` }} />
            </div>
          </div>
        ))}
        {reps.length === 0 && <div className="sm:col-span-2 lg:col-span-3"><Empty icon={DollarSign} title="No team yet" body="Add the reps, then close a deal." /></div>}
      </div>
      {reps.length > 0 && (
        <div className="text-xs text-slate-500 px-1">Team: {money(team.earned)} earned, {money(team.residual)} a month in residuals across {team.active} paying store{team.active === 1 ? '' : 's'}.</div>
      )}

      {/* deals */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-900">Deals</h3>
          <span className="text-xs text-slate-400">{deals.length}</span>
        </div>
        {deals.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-slate-500">No deals yet. When a store converts, the outcome asks for the plan and the amount, and it shows up here.</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left font-semibold px-4 py-2.5">Store</th>
                <th className="text-left font-semibold px-3 py-2.5 hidden md:table-cell">Plan</th>
                <th className="text-right font-semibold px-3 py-2.5">A month</th>
                <th className="text-left font-semibold px-3 py-2.5 hidden sm:table-cell">Since</th>
                <th className="text-left font-semibold px-3 py-2.5">Closed by</th>
                <th className="text-right font-semibold px-3 py-2.5 hidden sm:table-cell">Months</th>
                <th className="text-right font-semibold px-4 py-2.5">Earned</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {deals.map((p) => {
                const e = dealEarnings(p, now);
                return (
                  <tr key={p.id} className="hover:bg-slate-50/70 cursor-pointer" onClick={() => onOpen(p.id)}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900 truncate max-w-[200px] sm:max-w-none">{p.name}</div>
                      {!e.active && <div className="text-[11px] text-red-700">Stopped {fmtDay(p.churned_at)}</div>}
                    </td>
                    <td className="px-3 py-3 text-slate-600 hidden md:table-cell">{planLabel(p.plan)}</td>
                    <td className="px-3 py-3 text-right font-semibold text-slate-900 whitespace-nowrap">{money(p.monthly_amount)}</td>
                    <td className="px-3 py-3 text-slate-600 hidden sm:table-cell whitespace-nowrap">{fmtDay(p.billing_start)}</td>
                    <td className="px-3 py-3"><RepChip rep={repById[closerOf(p)]} withName /></td>
                    <td className="px-3 py-3 text-right text-slate-600 hidden sm:table-cell">{e.months}</td>
                    <td className="px-4 py-3 text-right font-bold text-slate-900 whitespace-nowrap">{money(e.earned)}{e.active && <div className="text-[11px] font-medium text-emerald-700">+{money(e.residual)}/mo</div>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* statement */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2">
          <h3 className="text-sm font-bold text-slate-900">Monthly statement</h3>
          <span className="text-xs text-slate-500">owed {money(totals.owed)} · paid {money(totals.settled)}</span>
          {reps.length > 1 && (
            <div className="ml-auto flex bg-slate-100 rounded-lg p-0.5">
              <button type="button" onClick={() => setRepFilter('all')} className={`px-2 py-1 rounded-md text-[11px] font-semibold ${repFilter === 'all' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>Everyone</button>
              {reps.map((r) => <button key={r.id} type="button" onClick={() => setRepFilter(r.id)} className={`px-2 py-1 rounded-md text-[11px] font-semibold ${repFilter === r.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>{r.name}</button>)}
            </div>
          )}
        </div>
        {rows.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-slate-500">Nothing owed yet.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {rows.map((r) => {
              const key = `${r.repId}|${r.period}`;
              return (
                <div key={key} className={`px-4 py-3 flex flex-wrap items-center gap-3 ${r.payout ? 'bg-emerald-50/40' : ''}`}>
                  <div className="w-24 font-bold text-slate-900">{periodLabel(r.period)}</div>
                  <RepChip rep={r.rep} withName />
                  <div className="min-w-0 flex-1 text-xs text-slate-500 truncate">
                    {r.lines.map((l) => `${l.prospect.name} ${l.first ? 'first month' : '10%'} ${money(l.commission)}`).join(' · ')}
                  </div>
                  <div className="text-base font-bold text-slate-900 whitespace-nowrap">{money(r.amount)}</div>
                  <button type="button" disabled={busyKey === key} onClick={() => togglePaid(r)} className={r.payout ? `${btnSmall} text-emerald-700 border-emerald-200 bg-emerald-50 hover:bg-emerald-100` : `${btnSmall} text-slate-700`} title={r.payout ? `Paid ${new Date(r.payout.paid_at).toLocaleDateString()}. Click to undo.` : 'Mark this month as paid out'}>
                    {r.payout ? <><Check className="w-3.5 h-3.5" /> Paid</> : <><CircleDollarSign className="w-3.5 h-3.5" /> Mark paid</>}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
